/**
 * ShippingAgentSettingsEdit component.
 * @module components/manage/ShippingAgentsSettings/ShippingAgentSettingsEdit
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useParams } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useClient } from '@plone/volto/hooks/client/useClient';
import { getContent } from '@plone/volto/actions/content/content';
import { Button, Container, Segment, Table } from 'semantic-ui-react';
import qs from 'query-string';
import { Form } from '@plone/volto/components/manage/Form';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import { default as Toast } from '@plone/volto/components/manage/Toast/Toast';
import { default as Toolbar } from '@plone/volto/components/manage/Toolbar/Toolbar';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import { getBaseUrl, getParentUrl } from '@plone/volto/helpers/Url/Url';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { toast } from 'react-toastify';
import clearSVG from '@plone/volto/icons/clear.svg';
import saveSVG from '@plone/volto/icons/save.svg';
import {
  clearShippingAgentSettings,
  getShippingAgentSettings,
  updateShippingAgentSettings,
} from '../../../actions/shop/shipping_agents_settings';
import isEmpty from 'lodash/isEmpty';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  editShippingAgentSettings: {
    id: 'Edit Shipping Agent Settings of {shippingAgentTitle} for {shop}',
    defaultMessage:
      'Edit Shipping Agent Settings of {shippingAgentTitle} for {shop}',
  },
  save: {
    id: 'Save',
    defaultMessage: 'Save',
  },
  shippingAgentSettings: {
    id: 'Shipping Agent Settings',
    defaultMessage: 'Shipping Agent Settings',
  },
  success: {
    id: 'Success',
    defaultMessage: 'Success',
  },
  successContent: {
    id: 'Shipping Agent Settings correctly saved.',
    defaultMessage: 'Shipping Agent Settings correctly saved.',
  },
});

/**
 * Component to edit the annotations.
 * @function ShippingAgentSettingsEdit
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ShippingAgentSettingsEdit = ({ location }) => {
  const { pathname } = location;
  const { id } = useParams();
  const baseUrl = getBaseUrl(pathname);
  const contentUrl = getParentUrl(getParentUrl(getParentUrl(pathname)));
  const dispatch = useDispatch();
  const isClient = useClient();
  const intl = useIntl();
  const history = useHistory();
  const form = useRef();
  const shippingAgent = useSelector(
    (state) => state.shippingAgentsSettings.shippingAgentSettings,
  );
  const title = useSelector((state) => state.content?.data?.title || '');
  const statusError = useSelector(
    (state) => state.shippingAgentsSettings?.get?.error?.status,
  );
  useEffect(() => {
    if (!title) {
      dispatch(getContent(contentUrl));
    }
  }, [title]);
  useEffect(() => {
    dispatch(getShippingAgentSettings(contentUrl, id));
    return () => dispatch(clearShippingAgentSettings());
  }, [pathname, dispatch, contentUrl, id]);

  function submit(formData) {
    if (!isEmpty(formData)) {
      dispatch(updateShippingAgentSettings(contentUrl, id, formData));
      dispatch(clearShippingAgentSettings());
      // dispatch(getShippingAgentSettings(contentUrl, id));
      toast.success(
        <Toast
          success
          title={intl.formatMessage(messages.success)}
          content={intl.formatMessage(messages.successContent)}
        />,
      );
      history.push(`${contentUrl}/shipping-agent-settings/${id}`);
    }
  }

  function cancel() {
    history.push(`${contentUrl}/shipping-agent-settings/${id}`);
  }

  if (isClient && statusError === 401) {
    return <Unauthorized />;
  }

  return (
    <Container id="page-shipping-agents">
      <Helmet title={intl.formatMessage(messages.shippingAgentSettings)} />
      <Segment.Group raised>
        <Segment className="primary">
          <FormattedMessage
            id="Shipping Agent Settings of {shippingAgentTitle} for {title}"
            defaultMessage="Shipping Agent Setting of {shippingAgentTitle} for {title}"
            values={{
              title: <q>{title}</q>,
              shippingAgentTitle: shippingAgent?.title || '',
            }}
          />
        </Segment>
      </Segment.Group>
      {shippingAgent && (
        <Form
          ref={form}
          title={intl.formatMessage(messages.editShippingAgentSettings, {
            shippingAgentTitle: shippingAgent.title,
            shop: title,
          })}
          schema={shippingAgent.schema}
          formData={shippingAgent.data || {}}
          pathname={pathname}
          hideActions
          onSubmit={submit}
          isEditForm={true}
        />
      )}
      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <>
                <Button
                  onClick={() => form.current.onSubmit()}
                  className="save"
                >
                  <Icon
                    name={saveSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.save)}
                  />
                </Button>
                <Link
                  to={`${contentUrl}/shipping-agent-settings/${id}`}
                  className="cancel"
                >
                  <Icon
                    name={clearSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.cancel)}
                  />
                </Link>
              </>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ShippingAgentSettingsEdit.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default ShippingAgentSettingsEdit;
